/**
 * Shop the Look — Tooltip Layout
 *
 * Custom element that provides:
 * - Desktop: hover tooltips near numbered hotspot circles
 * - Mobile: fixed bottom popup sheet with prev/next navigation
 *
 * No external dependencies (no Tippy.js).
 *
 * @module shop-the-look-tooltip
 */

const TAG = 'shop-the-look-section';

class ShopTheLookTooltip extends HTMLElement {
  connectedCallback() {
    this.hotspotsDesktop = [...this.querySelectorAll('.hotspot-desktop')];
    this.hotspotsMobile = [...this.querySelectorAll('.hotspot-mobile')];
    this.currentMobileIndex = 0;
    this.activeDesktopHotspot = null;
    this.activeMobileHotspot = null;
    /** @type {AbortController} */
    this._ac = new AbortController();

    this.#initDesktopTooltips();
    this.#initMobilePopup();
    this.#bindGlobalListeners();
  }

  disconnectedCallback() {
    this._ac.abort();
  }

  // ── Desktop Tooltips ────────────────────────────────────────────────

  #initDesktopTooltips() {
    const signal = this._ac.signal;

    this.hotspotsDesktop.forEach((hotspot) => {
      this.#computePlacement(hotspot);

      const content = hotspot.querySelector('.product-block-content');
      if (!content) return;

      // Hover: show tooltip on mouseenter, hide on mouseleave
      // Keep tooltip open while cursor is over the tooltip content itself
      let hideTimeout;

      const show = () => {
        clearTimeout(hideTimeout);
        this.#showDesktopTooltip(hotspot);
      };

      const scheduleHide = () => {
        hideTimeout = setTimeout(() => this.#hideDesktopTooltip(hotspot), 120);
      };

      hotspot.addEventListener('mouseenter', show, { signal });
      hotspot.addEventListener('mouseleave', scheduleHide, { signal });
      content.addEventListener('mouseenter', show, { signal });
      content.addEventListener('mouseleave', scheduleHide, { signal });

      // Click opens / pins tooltip (touch / keyboard); do not dismiss on second click — hover
      // already shows it and mouseleave hides; toggling off on click felt broken for mouse users.
      hotspot.querySelector('.hotspot-circle')?.addEventListener('click', (e) => {
        e.preventDefault();
        if (hotspot.dataset.selected !== 'true') {
          this.#hideAllDesktopTooltips();
          this.#showDesktopTooltip(hotspot);
        }
      }, { signal });
    });

    // Recalculate placement on resize
    window.addEventListener('resize', () => {
      this.hotspotsDesktop.forEach((h) => this.#computePlacement(h));
    }, { signal });
  }

  /**
   * Compute optimal tooltip placement based on hotspot position within container.
   * Sets data-tooltip-placement attribute used by CSS for positioning.
   * @param {HTMLElement} hotspot
   */
  #computePlacement(hotspot) {
    const container = this.getBoundingClientRect();
    const rect = hotspot.getBoundingClientRect();
    const relLeft = rect.left - container.left;
    const relTop = rect.top - container.top;

    const nearLeft = relLeft < container.width * 0.1;
    const nearTop = relTop < container.height * 0.3;

    const horizontal = nearLeft ? 'start' : 'end';
    const vertical = nearTop ? 'bottom' : 'top';

    hotspot.dataset.tooltipPlacement = `${vertical}-${horizontal}`;
  }

  /** @param {HTMLElement} hotspot */
  #showDesktopTooltip(hotspot) {
    this.#hideAllDesktopTooltips();
    hotspot.setAttribute('data-selected', 'true');
    this.activeDesktopHotspot = hotspot;
  }

  /** @param {HTMLElement} hotspot */
  #hideDesktopTooltip(hotspot) {
    hotspot.setAttribute('data-selected', 'false');
    if (this.activeDesktopHotspot === hotspot) {
      this.activeDesktopHotspot = null;
    }
  }

  #hideAllDesktopTooltips() {
    this.hotspotsDesktop.forEach((h) => h.setAttribute('data-selected', 'false'));
    this.activeDesktopHotspot = null;
  }

  // ── Mobile Popup ────────────────────────────────────────────────────

  #initMobilePopup() {
    const signal = this._ac.signal;

    this.hotspotsMobile.forEach((hotspot, index) => {
      hotspot.addEventListener('click', () => {
        this.currentMobileIndex = index;
        this.#setActiveMobileHotspot(hotspot);
        this.#showMobilePopup(hotspot);
      }, { signal });
    });

    // Popup buttons
    const popup = this.#getPopup();
    if (!popup) return;

    popup.querySelector('[data-stl-popup-cancel]')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.#closeMobilePopup();
    }, { signal });

    popup.querySelector('[data-stl-popup-prev]')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.#navigateMobilePopup(-1);
    }, { signal });

    popup.querySelector('[data-stl-popup-next]')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.#navigateMobilePopup(1);
    }, { signal });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!popup || popup.classList.contains('hidden')) return;
      if (!e.target.closest('.hotspot-mobile') && !e.target.closest('#mobile-popup')) {
        this.#closeMobilePopup();
      }
    }, { signal });
  }

  /** @returns {HTMLElement|null} */
  #getPopup() {
    return this.querySelector('#mobile-popup') || document.getElementById('mobile-popup');
  }

  /** @param {HTMLElement} hotspot */
  #setActiveMobileHotspot(hotspot) {
    if (this.activeMobileHotspot && this.activeMobileHotspot !== hotspot) {
      this.activeMobileHotspot.setAttribute('data-selected', 'false');
    }
    this.activeMobileHotspot = hotspot;
    hotspot.setAttribute('data-selected', 'true');
  }

  /** @param {HTMLElement} hotspot */
  #showMobilePopup(hotspot) {
    const popup = this.#getPopup();
    if (!popup) return;

    this.#updatePopupContent({
      image: hotspot.dataset.image || '',
      title: hotspot.dataset.title || '',
      price: hotspot.dataset.price || '',
      comparePrice: hotspot.dataset.compareprice || '',
      url: hotspot.dataset.url || '#',
    });

    popup.classList.remove('hidden');
  }

  #closeMobilePopup() {
    const popup = this.#getPopup();
    if (popup) popup.classList.add('hidden');
    if (this.activeMobileHotspot) {
      this.activeMobileHotspot.setAttribute('data-selected', 'false');
      this.activeMobileHotspot = null;
    }
  }

  /** @param {number} direction — -1 for prev, 1 for next */
  #navigateMobilePopup(direction) {
    const count = this.hotspotsMobile.length;
    if (count === 0) return;

    let next = (this.currentMobileIndex + direction + count) % count;
    let attempts = 0;
    while (attempts < count) {
      const h = this.hotspotsMobile[next];
      if (h.dataset.title && h.dataset.image) break;
      next = (next + direction + count) % count;
      attempts++;
    }
    if (attempts >= count) return;

    this.currentMobileIndex = next;
    const hotspot = this.hotspotsMobile[next];
    this.#setActiveMobileHotspot(hotspot);
    this.#updatePopupContent({
      image: hotspot.dataset.image || '',
      title: hotspot.dataset.title || '',
      price: hotspot.dataset.price || '',
      comparePrice: hotspot.dataset.compareprice || '',
      url: hotspot.dataset.url || '#',
    });
  }

  /**
   * @param {object} data
   * @param {string} data.image
   * @param {string} data.title
   * @param {string} data.price
   * @param {string} data.comparePrice
   * @param {string} data.url
   */
  #updatePopupContent({ image, title, price, comparePrice, url }) {
    const normalizePrice = (value) => value.replace(/[^0-9.-]/g, '');

    const popup = this.#getPopup();
    if (!popup) return;
    const imgEl = popup.querySelector('#popup-image');
    if (imgEl && image) {
      const base = image.replace(/width=\d+/, '');
      imgEl.src = `${base}width=230`;
      imgEl.srcset = `${base}width=230 1x, ${base}width=460 2x`;
      imgEl.alt = title || 'Product Image';
    }

    ['popup-title-link', 'popup-image-link', 'popup-link'].forEach((id) => {
      const el = popup.querySelector(`#${id}`);
      if (el) el.href = url || '#';
    });

    const titleEl = popup.querySelector('#popup-title');
    if (titleEl) titleEl.textContent = title;

    const priceEl = popup.querySelector('#popup-price');
    if (priceEl) priceEl.textContent = price;

    const compareEl = popup.querySelector('#popup-compare');
    if (compareEl) {
      const normalizedPrice = normalizePrice(price);
      const normalizedComparePrice = normalizePrice(comparePrice);

      if (comparePrice && normalizedComparePrice !== normalizedPrice) {
        compareEl.textContent = comparePrice;
        compareEl.style.display = 'block';
      } else {
        compareEl.style.display = 'none';
      }
    }
  }

  // ── Global Listeners ────────────────────────────────────────────────

  #bindGlobalListeners() {
    const signal = this._ac.signal;

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.#hideAllDesktopTooltips();
        this.#closeMobilePopup();
      }
    }, { signal });

    // Close mobile popup when nav/search/cart opens
    ['data-state-mobile-nav', 'data-state-search', 'data-state-cart-modal'].forEach((attr) => {
      const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'attributes' && document.body.getAttribute(attr) === 'open') {
            this.#closeMobilePopup();
          }
        }
      });
      observer.observe(document.body, { attributes: true, attributeFilter: [attr] });

      // Disconnect on abort
      signal.addEventListener('abort', () => observer.disconnect());
    });

    // Close mobile popup on resize to desktop
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 768) this.#closeMobilePopup();
    }, { signal });
  }
}

if (!customElements.get(TAG)) {
  customElements.define(TAG, ShopTheLookTooltip);
}
